/**
 * Connections — the accounts a tenant authenticates once (NetSuite, M-Link,
 * Enbase, Azure AI Search, Samsara, a Custom API contract endpoint). Set a
 * source up here, then turn it on for the capabilities it feeds on the
 * Capabilities page. One connection can power several capabilities.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Modal,
  Skeleton,
  StatusIndicator,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import type { TenantIntegrationSummary } from '../../lib/types.js';
import { useIntegrationCatalog } from './useIntegrationCatalog.js';
import { useIntegrationsListLogic } from './useIntegrationsListLogic.js';
import {
  buildModel,
  type ConnectionDef,
  connectionStatus,
  connectionTitle,
  SystemAvatar,
  systemDisplay,
  TestResultBanner,
} from './shared.js';

/** A card is one configured instance, or a kind with none yet. */
interface ConnectionCard {
  def: ConnectionDef;
  row: TenantIntegrationSummary | undefined;
}

/**
 * Connection kinds a tenant may hold several of, with the copy for their "add"
 * tile. These kinds never get an empty "Connect" card: the dotted tile is how
 * you add the first one and every one after, so a tenant with none sees one
 * tile, not a card and a tile offering the same action. Single-instance vendors
 * (NetSuite, Samsara, …) keep their Connect card.
 */
const REPEATABLE_CONNECTIONS: Record<string, { label: string; hint: string }> = {
  custom_api: { label: 'Add Custom API', hint: 'Another endpoint speaking the data contract' },
  custom_mcp: { label: 'Add Custom MCP server', hint: 'Another MCP server with its own tools' },
};

/**
 * The host a connection points at, plus how it authenticates. With two Custom
 * API cards this is what distinguishes them at a glance — the titles differ but
 * the host is the thing an operator recognises.
 */
const ConnectionSubtitle = ({ row }: { row: TenantIntegrationSummary }) => {
  const url = row.config.url;
  if (url === undefined || url === '') return null;
  let host = url;
  try {
    host = new URL(url).host;
  } catch {
    // Not a parseable URL — show it raw rather than hiding a misconfiguration.
  }
  const auth = row.config.authHeader ?? row.config.authStyle;
  return (
    <p className="text-ink-subtle mt-0.5 truncate font-mono text-xs">
      {host}
      {auth !== undefined && auth !== '' ? ` · ${auth}` : ''}
    </p>
  );
};

/** Name the tested instance the way the card does, so the banner matches it. */
const labelForId = (integrationId: string, cards: ConnectionCard[]): string => {
  const match = cards.find((card) => card.row?.id === integrationId);
  if (!match) return 'Connection';
  return connectionTitle(match.def, match.row?.name);
};

/** The two lists that make a Custom MCP connection usable, in one line. */
const mcpSummary = (row: TenantIntegrationSummary): string => {
  const groups = row.mcpAllowedGroupIds.length;
  const groupsText = `${String(groups)} ${groups === 1 ? 'group has' : 'groups have'} access`;
  if (!row.mcpTools) return `No tool list yet · ${groupsText}`;
  return `${String(row.mcpApprovedTools.length)} of ${String(row.mcpTools.length)} tools approved · ${groupsText}`;
};

const formatTested = (iso: string | null): string => {
  if (!iso) return 'Never tested';
  return `Tested ${new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

export const ConnectionsPage = () => {
  const navigate = useNavigate();
  const me = useMe();
  const tenantId = me.data?.tenant.id;
  const catalog = useIntegrationCatalog();
  const accounts = useIntegrationsListLogic(tenantId);

  const integrations = useMemo(() => catalog.data?.integrations ?? [], [catalog.data]);
  const connections = useMemo(() => buildModel(integrations).connections, [integrations]);
  const rows = useMemo(() => accounts.data?.integrations ?? [], [accounts.data]);

  /**
   * One card per CONFIGURED INSTANCE, not per catalog kind — a tenant can hold
   * two Custom API connections, and keying by catalog key would collapse them
   * into one card. A single-instance kind with no instances still gets a card,
   * offering Connect; a repeatable kind with none is added from its tile instead.
   */
  const cards = useMemo((): ConnectionCard[] => {
    const out: ConnectionCard[] = [];
    for (const def of connections) {
      const instances = rows.filter((row) => row.key === def.key);
      if (instances.length === 0) {
        if (!(def.key in REPEATABLE_CONNECTIONS)) out.push({ def, row: undefined });
        continue;
      }
      for (const row of instances) out.push({ def, row });
    }
    return out;
  }, [connections, rows]);

  /**
   * How many capabilities draw credentials from a connection. Derived from the
   * rows we already have — a binding row points at its connection — so blocking
   * Disconnect costs no extra request.
   */
  const dependentCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      if (row.connectionId === null) continue;
      counts.set(row.connectionId, (counts.get(row.connectionId) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  /** The disconnected connection awaiting removal confirmation; null = closed. */
  const [removeTarget, setRemoveTarget] = useState<{ id: string; title: string } | null>(null);

  if (me.isLoading || !me.data || catalog.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-2 h-7 w-48" />
        <Skeleton className="mb-6 h-4 w-96" />
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      </AppShell>
    );
  }

  if (catalog.error) {
    return (
      <AppShell>
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>Could not load connections.</Banner.Title>
            <Banner.Description>{catalog.error.message}</Banner.Description>
          </Banner.Body>
        </Banner>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <h2 className="text-ink text-2xl font-bold">Connections</h2>
      <p className="text-ink-muted mb-6 mt-1 max-w-[76ch] text-sm">
        The accounts this tenant has connected. Set a source up once here — credentials, or for a
        Custom API the base URL + key — then turn it on for the capabilities it feeds under
        Capabilities. One connection can power several capabilities.
      </p>

      <TestResultBanner
        testResult={
          accounts.testResult
            ? {
                label: labelForId(accounts.testResult.integrationId, cards),
                result: accounts.testResult.result,
              }
            : null
        }
      />

      {accounts.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>Could not load connected accounts.</Banner.Title>
            <Banner.Description>{accounts.error.message}</Banner.Description>
          </Banner.Body>
        </Banner>
      )}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
        {cards.map(({ def, row }) => {
          const configured = row !== undefined;
          const status = connectionStatus(row);
          const isMcp = row?.key === 'custom_mcp';
          // The instance's own name wins: with two Custom API connections the
          // system label reads identically on both, so it can't be the identity.
          const title = connectionTitle(def, row?.name);
          const dependents = row ? (dependentCount.get(row.id) ?? 0) : 0;
          return (
            <Card key={row?.id ?? def.key} padding="md">
              <div className="flex items-start gap-3">
                <SystemAvatar system={def.system} title={title} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-ink truncate text-[15px] font-semibold">{title}</h3>
                    {/*
                      The kind, but only when the title doesn't already say it. An
                      unnamed connection is titled by its system, so a badge
                      repeating that is noise; a named one ("Ricky Curator Docs")
                      genuinely benefits from being labelled Custom API.

                      Not the slug — that's an internal identifier for tagging
                      merged records and naming the stored secret. The host below
                      is what distinguishes two connections of the same kind.
                    */}
                    {configured && row.name !== '' && (
                      <Badge variant="neutral">{systemDisplay(def.system)}</Badge>
                    )}
                  </div>
                  {configured && <ConnectionSubtitle row={row} />}
                  <div className="mt-1 flex flex-col gap-0.5">
                    <StatusIndicator tone={status.tone}>{status.label}</StatusIndicator>
                    {configured && (
                      <span className="text-ink-subtle text-xs">
                        {formatTested(row.lastTestedAt)}
                      </span>
                    )}
                    {configured && isMcp && (
                      <span className="text-ink-muted text-xs">{mcpSummary(row)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="border-outline mt-4 flex items-center justify-end gap-2 border-t pt-3">
                {configured ? (
                  <>
                    {/* A Custom MCP connection has a screen of its own; testing
                        and editing happen there, so the card offers one way in. */}
                    {isMcp ? (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => navigate(`/connections/${row.id}/tools`)}
                      >
                        Manage
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void accounts.test(row.id)}
                          disabled={accounts.testingId === row.id || row.isConnected === false}
                        >
                          {accounts.testingId === row.id ? 'Testing…' : 'Test'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/connections/${row.id}/edit`)}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        void (row.isConnected === false
                          ? accounts.reconnect(row.id)
                          : accounts.remove(row.id))
                      }
                      disabled={
                        accounts.removingId === row.id || accounts.reconnectingId === row.id
                      }
                      title={
                        dependents > 0
                          ? `${dependents} capabilities use this connection. Disconnecting pauses access without removing their setup.`
                          : undefined
                      }
                    >
                      {accounts.removingId === row.id
                        ? 'Disconnecting…'
                        : accounts.reconnectingId === row.id
                          ? 'Reconnecting…'
                          : row.isConnected === false
                            ? 'Reconnect'
                            : 'Disconnect'}
                    </Button>
                    {row.isConnected === false && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setRemoveTarget({ id: row.id, title: title })}
                        disabled={accounts.deletingId === row.id || dependents > 0}
                        title={
                          dependents > 0
                            ? `${dependents} ${dependents === 1 ? 'capability still reads' : 'capabilities still read'} from this connection. Point them elsewhere first.`
                            : undefined
                        }
                      >
                        {accounts.deletingId === row.id ? 'Removing…' : 'Remove'}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button size="sm" onClick={() => navigate(`/connections/${def.key}/connect`)}>
                    Connect
                  </Button>
                )}
              </div>
            </Card>
          );
        })}

        {/* The first instance and every one after come from here. */}
        {Object.entries(REPEATABLE_CONNECTIONS).map(([key, copy]) => (
          <button
            key={key}
            type="button"
            onClick={() => navigate(`/connections/${key}/connect`)}
            className="border-outline text-link hover:border-outline-strong hover:bg-surface-hover flex min-h-[152px] flex-col items-center justify-center gap-1 rounded-xl border border-dashed p-4 text-sm font-medium transition-colors"
          >
            + {copy.label}
            <span className="text-ink-subtle text-xs font-normal">{copy.hint}</span>
          </button>
        ))}
      </div>
      <Modal open={removeTarget !== null} onOpenChange={() => setRemoveTarget(null)}>
        <Modal.Header>
          <Modal.Title>Remove connection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-ink-muted">
            This deletes {removeTarget?.title ?? 'this connection'} and its stored credentials.
            Nothing reads from it today. This cannot be undone; you can connect it again later from
            scratch.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (removeTarget) void accounts.deletePermanently(removeTarget.id);
              setRemoveTarget(null);
            }}
          >
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};
